'use client'

import type { ReactNode } from 'react'
import { useCarouselScroll } from '@/hooks/useCarouselScroll'

interface CardCarouselProps<T> {
  items: T[]
  keyExtractor: (item: T, index: number) => string
  renderDarkCard: (item: T, index: number) => ReactNode
  renderLightCard: (item: T, index: number) => ReactNode
  ariaLabel?: string
  autoplayDelay?: number
}

export function CardCarousel<T>({
  items,
  keyExtractor,
  renderDarkCard,
  renderLightCard,
  ariaLabel = 'Content carousel',
  autoplayDelay = 10000,
}: CardCarouselProps<T>) {
  const { scrollRef, activeIndex, scrollToIndex, handleKeyDown } = useCarouselScroll({
    itemCount: items.length,
    autoplayDelay,
    // Dots rather than edge-disabled arrows, and autoplay already cycles.
    loop: true,
  })

  return (
    <div>
      {/* Mobile/tablet: carousel with overlaid dots */}
      <div
        className="relative lg:hidden bg-black rounded-[12px] border border-black overflow-hidden"
        role="region"
        aria-label={ariaLabel}
        aria-roledescription="carousel"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div
          ref={scrollRef}
          role="list"
          className="flex overflow-x-auto scroll-smooth snap-x snap-mandatory scrollbar-hide"
        >
          {items.map((item, index) => (
            <div
              key={keyExtractor(item, index)}
              data-card
              role="listitem"
              aria-label={`Slide ${index + 1} of ${items.length}`}
              className="w-full shrink-0 snap-start"
            >
              {renderDarkCard(item, index)}
            </div>
          ))}
        </div>

        {items.length > 1 && (
          <div
            className="absolute top-4 right-4 flex items-center gap-1 z-10"
            role="group"
            aria-label="Carousel pagination"
          >
            {items.map((_, index) => (
              <button
                key={index}
                aria-current={index === activeIndex ? 'step' : undefined}
                onClick={() => scrollToIndex(index)}
                aria-label={`Go to slide ${index + 1}`}
                className={`h-3 rounded-full border border-white transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black ${
                  index === activeIndex ? 'w-7 bg-white' : 'w-3 bg-transparent'
                }`}
              />
            ))}
          </div>
        )}

        <div className="sr-only" aria-live="polite" aria-atomic="true">
          {`Slide ${activeIndex + 1} of ${items.length}`}
        </div>
      </div>

      {/* Desktop: 3-column grid */}
      <div className="hidden lg:grid lg:grid-cols-3 gap-8">
        {items.map((item, index) => (
          <div key={keyExtractor(item, index)}>
            {renderLightCard(item, index)}
          </div>
        ))}
      </div>
    </div>
  )
}
